import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/kuaibao";

const navItems = [
  { label: "首页", to: "/" },
  { label: "吃瓜快报", to: "/kuaibao" },
  { label: "关于我们", to: "/about" },
];

interface SiteNavbarProps {
  overlay?: boolean;
}

export default function SiteNavbar({ overlay = false }: SiteNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !overlay;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/80"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading font-bold text-lg">
              5
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading font-bold text-lg tracking-wide ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                51.cg
              </span>
              <span
                className={`font-label text-[11px] tracking-widest ${
                  solid ? "text-foreground-500" : "text-background-200"
                }`}
              >
                今日吃瓜单
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 font-label text-sm">
            {navItems.map((item) => {
              const active =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3.5 py-2 rounded-md transition-colors whitespace-nowrap ${
                    active
                      ? solid
                        ? "text-primary-600 font-medium"
                        : "text-background-50 font-medium"
                      : solid
                        ? "text-foreground-600 hover:text-foreground-950"
                        : "text-background-200 hover:text-background-50"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`px-3.5 py-2 rounded-md flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap ${
                  solid
                    ? "text-foreground-600 hover:text-foreground-950"
                    : "text-background-200 hover:text-background-50"
                }`}
              >
                分类
                <i className="ri-arrow-down-s-line text-base leading-none"></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full pt-2 w-[420px]">
                  <div className="bg-background-50 border border-background-200 rounded-lg p-3 grid grid-cols-2 gap-1.5">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/kuaibao?cat=${cat.slug}`}
                        className="flex items-start gap-2.5 p-2 rounded-md hover:bg-background-100 transition-colors"
                      >
                        <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                          <i className={`${cat.icon} text-base leading-none`}></i>
                        </span>
                        <span className="flex flex-col">
                          <span className="font-label text-sm text-foreground-900 font-medium">
                            {cat.name}
                          </span>
                          <span className="font-label text-xs text-foreground-500 line-clamp-1">
                            {cat.desc}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/kuaibao"
              className={`hidden md:flex w-10 h-10 items-center justify-center rounded-md transition-colors ${
                solid
                  ? "text-foreground-600 hover:bg-background-100"
                  : "text-background-100 hover:bg-background-50/10"
              }`}
              aria-label="搜索快报"
            >
              <i className="ri-search-line text-lg leading-none"></i>
            </Link>
            <Link
              to="/#subscribe"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-notification-3-line text-base leading-none"></i>
              订阅快报
            </Link>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md ${
                solid ? "text-foreground-900" : "text-background-50"
              }`}
              aria-label="打开导航菜单"
            >
              <i
                className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl leading-none`}
              ></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 py-4">
          <nav className="flex flex-col gap-1 font-label">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="px-3 py-2.5 rounded-md text-foreground-800 hover:bg-background-100 text-sm"
              >
                {item.label}
              </Link>
            ))}
            <div className="px-3 pt-3 pb-1 text-xs text-foreground-400 tracking-widest">
              内容分类
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/kuaibao?cat=${cat.slug}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-background-100 text-sm text-foreground-700"
                >
                  <i className={`${cat.icon} text-secondary-600 leading-none`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link
              to="/#subscribe"
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium"
            >
              <i className="ri-notification-3-line leading-none"></i>
              订阅快报
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}